import React, { Suspense, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { BookOpen, Users, Calendar, GraduationCap, DollarSign, LayoutDashboard, CheckCircle2, PlayCircle, ArrowRight, CalendarCheck, Wallet, MessageSquare, Smartphone, Building, Zap, Settings, ShieldCheck, MessageCircle, Sparkles, Loader2, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import LoadingCard from '../components/shared/LoadingCard';
import RegisterSignupModal from '../components/client/RegisterSignupModal';
import ClientLoginModal from '../components/client/ClientLoginModal';
const Hero3D = React.lazy(() => import('../components/client/Hero3D'));



export default function ClientPortal() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Form submission state
  const [formData, setFormData] = useState({
    schoolName: '',
    yourName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('trialToken'));
  const [hasTrial, setHasTrial] = useState(false);
  const [loadingTrialStatus, setLoadingTrialStatus] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError("Mobile number should be exactly 10 digits.");
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await axiosClient.post('/trials/request', formData);
      setIsSubmitted(true);
      setHasTrial(true);
      window.dispatchEvent(new Event('auth-change')); // Trigger any updates
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const listener = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    const handleAuthChange = () => setIsLoggedIn(!!localStorage.getItem('trialToken'));
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      axiosClient.get('/trials/me')
        .then(res => {
          setHasTrial(res.data.hasTrial);
        })
        .catch(err => console.error("Error fetching trial status", err))
        .finally(() => setLoadingTrialStatus(false));
    } else {
      setHasTrial(false);
      setLoadingTrialStatus(false);
    }
  }, [isLoggedIn]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <section className="relative max-w-7xl mx-auto px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div className="section-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 1rem', background: 'transparent', border: '1px solid rgba(37, 99, 235, 0.2)' }}>
              <Sparkles size={14} className="text-primary-600" />
              School ERP for modern institutions
            </div>
            <div className="section-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 1rem', background: 'transparent', border: '1px solid rgba(37, 99, 235, 0.2)' }}>
              <span className="flex h-2 w-2 rounded-full bg-primary-500"></span>
              SchoolERP v2.0 is now live
            </div>
          </div>

          <h1 className="hero-gradient-text" style={{ fontSize: '4rem', lineHeight: '1.1', marginBottom: '1.5rem', fontWeight: 800 }}>
            Empower Your School with Smart ERP Solutions
          </h1>
          <p className="section-desc" style={{ fontSize: '1.15rem', marginBottom: '2.5rem', color: 'var(--light-gray)' }}>
            Transform education management with a comprehensive cloud-based platform that streamlines operations, boosts productivity, and improves learning outcomes.
          </p>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '3rem' }}>
            <button 
              onClick={() => {
                if (isLoggedIn) {
                  document.getElementById('trial-section')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  setLoginModalOpen(true);
                }
              }} 
              className="btn btn-primary btn-lg" 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Get Started Free
              <ArrowRight size={18} />
            </button>
            <button onClick={() => document.getElementById('why-choose-us')?.scrollIntoView({ behavior: 'smooth' })} className="btn btn-outline btn-lg" style={{ display: 'flex', alignItems: 'center' }}>
              Choose Us
            </button>
          </div>

          <div className="pill-row">
            <div className="pill-item">
              <div className="icon-box" style={{ margin: 0, width: '42px', height: '42px', borderRadius: '10px' }}>
                <CalendarCheck size={20} />
              </div>
              <div>
                <strong>30-day</strong>
                <span>Free trial</span>
              </div>
            </div>
            <div className="pill-item">
              <div className="icon-box" style={{ margin: 0, width: '42px', height: '42px', borderRadius: '10px' }}>
                <ShieldCheck size={20} />
              </div>
              <div>
                <strong>Trusted</strong>
                <span>by schools worldwide</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="h-[400px] lg:h-[600px] w-full relative">
          {!prefersReducedMotion ? (
            <Suspense fallback={<div className="w-full h-full animate-pulse bg-primary-50 rounded-3xl" />}>
              <Hero3D />
            </Suspense>
          ) : (
            <div className="w-full h-full bg-primary-100 rounded-3xl flex items-center justify-center">
              <LayoutDashboard size={120} className="text-primary-300" />
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section Removed */}

      {/* Trial Modal */}
      {modalOpen && <RegisterSignupModal onClose={() => setModalOpen(false)} />}

      {/* Login Modal */}
      {loginModalOpen && (
        <ClientLoginModal
          onClose={() => setLoginModalOpen(false)}
          onSwitchToSignup={() => {
            setLoginModalOpen(false);
            setModalOpen(true);
          }}
        />
      )}

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="section-wrapper">
        <div className="grid-2">
          <div>
            <span className="section-label">Why Choose Roriri ERP</span>
            <h2 className="section-title">Built by educators, for educators.</h2>
            <p className="section-desc" style={{ marginBottom: 0 }}>
              We understand the unique challenges of school management. Roriri brings speed, flexibility, security, and support to every institution.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <Zap size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>Lightning Fast Performance</h3>
                <p style={{ margin: 0 }}>Cloud-native architecture ensures instant load times and seamless performance, even with hundreds of concurrent users.</p>
              </div>
            </div>

            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <Settings size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>Highly Customizable</h3>
                <p style={{ margin: 0 }}>Adapt Roriri to match your school's unique workflows and requirements with custom fields, reports, and integrations.</p>
              </div>
            </div>

            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <ShieldCheck size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>High-Level Security</h3>
                <p style={{ margin: 0 }}>Your data is protected with encryption, automated backups, and role-based access control for complete peace of mind.</p>
              </div>
            </div>

            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <MessageCircle size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>Exceptional Support</h3>
                <p style={{ margin: 0 }}>Dedicated support available via chat, email, and guided onboarding with tutorials to help you succeed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={{ background: 'rgba(241, 245, 249, 0.4)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="section-wrapper">
          <div className="text-center-wrapper">
            <span className="section-label">Loved by Schools Worldwide</span>
            <h2 className="section-title">See what educators are saying about Roriri ERP Solutions</h2>
          </div>

          <div className="testimonial-grid">
            <div className="testimonial-card">
              <p className="testimonial-quote">
                “Roriri ERP has completely transformed how we manage our school. The attendance system alone has saved us countless hours, and parents love the real-time updates!”
              </p>
              <div className="testimonial-author">Little Flower School, Indore</div>
            </div>

            <div className="testimonial-card">
              <p className="testimonial-quote">
                “The fee management module is exceptional. Online payments, automated reminders, and detailed reports have made our finance department's work so much easier.”
              </p>
              <div className="testimonial-author">Fateh Group of Institutes, Punjab</div>
            </div>

            <div className="testimonial-card">
              <p className="testimonial-quote">
                “Migration from our old system was seamless thanks to the support team. They guided us through every step and had us up and running within a week!”
              </p>
              <div className="testimonial-author">Radiant Way School, Raipur</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Section */}
      <section id="trial-section" className="section-wrapper">
        <div className="grid-2">
          <div>
            <span className="section-label">Ready to Transform Your School?</span>
            <h2 className="section-title" style={{ fontSize: '2.2rem' }}>
              Start your 30-day free trial instantly, no strings attached.
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2.5rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <CheckCircle2 className="text-primary" size={24} style={{ flexShrink: 0, marginTop: '0.2rem' }} />
                <div>
                  <h4 style={{ fontSize: '1.05rem', marginBottom: '0.25rem' }}>No Credit Card Required</h4>
                  <p style={{ margin: 0, fontSize: '0.92rem' }}>Begin immediately and discover the power of Roriri.</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <CheckCircle2 className="text-primary" size={24} style={{ flexShrink: 0, marginTop: '0.2rem' }} />
                <div>
                  <h4 style={{ fontSize: '1.05rem', marginBottom: '0.25rem' }}>Easy Migration</h4>
                  <p style={{ margin: 0, fontSize: '0.92rem' }}>Move from your existing system smoothly with guided setup and support.</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <CheckCircle2 className="text-primary" size={24} style={{ flexShrink: 0, marginTop: '0.2rem' }} />
                <div>
                  <h4 style={{ fontSize: '1.05rem', marginBottom: '0.25rem' }}>Dedicated Support</h4>
                  <p style={{ margin: 0, fontSize: '0.92rem' }}>We’re available every step of the way to ensure your success.</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="form-card">
              {isLoggedIn ? (
                loadingTrialStatus ? (
                  <LoadingCard message="Loading your portal..." />
                ) : hasTrial ? (
                  <div className="form-success-container">
                    <div className="success-icon-circle">
                    <CheckCircle2 size={36} />
                  </div>
                  <h3 className="text-xl font-bold mt-4 mb-2">You're already registered!</h3>
                  <p style={{ marginTop: '0.5rem', marginBottom: '1.5rem', color: '#64748b' }}>
                    You have already submitted a trial request. Please check your email for access details once your trial is approved by the admin.
                  </p>
                </div>
              ) : isSubmitted ? (
                <div className="form-success-container">
                  <div className="success-icon-circle">
                    <CheckCircle2 size={36} />
                  </div>
                  <h3>Thank you!</h3>
                  <p style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
                    Your free trial request has been submitted successfully. Our team will contact you shortly.
                  </p>
                  <button className="btn btn-outline" onClick={() => setIsSubmitted(false)}>
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <>
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Start Your Free Trial</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="schoolName">School / Organization Name</label>
                      <input
                        type="text"
                        id="schoolName"
                        name="schoolName"
                        className="form-control"
                        placeholder="Enter your School or Organization name"
                        value={formData.schoolName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="form-control"
                        placeholder="+91 9876543210"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="password">Create Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          className="form-control"
                          style={{ paddingRight: '2.5rem' }}
                          placeholder="New Password for the Trial Instance"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                        <button 
                          type="button" 
                          onClick={() => setShowPassword(!showPassword)} 
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    
                    {error && (
                      <div className="p-3 mb-4 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary btn-block"
                      disabled={isSubmitting}
                      style={{ marginTop: '1rem' }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" /> Submitting...
                        </span>
                      ) : 'Start Free 30-Day Trial'}
                    </button>

                    <p className="disclaimer">
                      By signing up, you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
                    </p>
                  </form>
                </>
              )) : (
                <div className="text-center py-8">
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Ready to get started?</h3>
                  <p className="text-slate-600 mb-6">Log in or sign up to request your 30-day free trial.</p>
                  <div className="flex gap-4 justify-center">
                    <button className="btn btn-outline" onClick={() => setLoginModalOpen(true)}>Log In</button>
                    <button className="btn btn-primary" onClick={() => setModalOpen(true)}>Sign Up</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {loginModalOpen && (
        <ClientLoginModal
          onClose={() => setLoginModalOpen(false)}
          onSwitchToSignup={() => {
            setLoginModalOpen(false);
            setModalOpen(true);
          }}
        />
      )}
    </div>
  );
}
